import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faTimes, faSpinner, faInfoCircle, faHandshake, faSmile } from '@fortawesome/free-solid-svg-icons';
import { availableEmojis, getEmojiData } from '../../utils/emojiUtils';

// Define animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseButton = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const FormContainer = styled.form`
  margin-bottom: 15px;
  max-width: 700px;
  width: 100%;
  position: relative;
  
  @media (max-width: 768px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0;
    padding: 10px 15px 15px;
    background-color: rgba(18, 18, 18, 0.95);
    backdrop-filter: blur(10px);
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    z-index: 1000;
    max-width: 100%;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  background-color: rgba(26, 26, 26, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: visible;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  
  &:focus-within {
    border-color: rgba(59, 130, 246, 0.3);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    border-radius: 20px;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 40px;
  padding: 12px 15px;
  background-color: transparent;
  border: none;
  color: var(--text-light);
  font-size: 15px;
  line-height: 1.5;
  resize: none;
  white-space: pre-wrap;
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: var(--text-dim);
  }
  
  @media (max-width: 768px) {
    padding: 10px 15px;
    min-height: 40px;
    max-height: 100px;
    font-size: 15px;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #007AFF;
  color: white;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  min-width: 36px;
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: #0056d6;
    transform: translateY(-1px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    background-color: #444;
    cursor: not-allowed;
    opacity: 0.5;
  }
  
  &.submitting {
    animation: ${pulseButton} 1.5s infinite;
  }
  
  svg {
    font-size: 16px;
  }
`;

const GuidelineButton = styled.button`
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-dim);
  font-size: 14px;
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  transition: background-color 0.2s;
  margin-left: 10px;
  
  &:hover {
    color: var(--primary-light);
    background-color: rgba(59, 130, 246, 0.1);
  }
  
  ${props => props.$active && `
    color: var(--primary-light);
    background-color: rgba(59, 130, 246, 0.1);
  `}
`;

const GuidelinesPopup = styled.div`
  position: absolute;
  bottom: calc(100% + 10px);
  left: 10px;
  width: 90%;
  max-width: 300px;
  background-color: rgba(18, 18, 18, 0.95);
  border-radius: 15px;
  padding: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  z-index: 1100;
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: var(--text-light);
  text-align: left;
  animation: ${fadeIn} 0.3s ease-out;
  
  h3 {
    color: var(--primary-light);
    font-weight: 600;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    font-size: 14px;
    
    svg {
      margin-right: 6px;
    }
  }
  
  ul {
    margin: 0;
    padding-left: 20px;
    margin-bottom: 10px;
    
    li {
      margin-bottom: 6px;
      font-size: 13px;
      line-height: 1.4;
    }
  }
  
  button {
    background-color: var(--primary-color);
    color: white;
    border: none;
    border-radius: 20px;
    padding: 8px 15px;
    font-size: 12px;
    cursor: pointer;
    display: block;
    margin: 0 auto;
    transition: all 0.3s ease;
    
    &:hover {
      background-color: var(--primary-light);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
    }
  }

  @media (max-width: 768px) {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    width: 85%;
    max-width: 320px;
    z-index: 9999;
    max-height: 80vh;
    overflow-y: auto;
    border: 2px solid var(--primary-color);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  backdrop-filter: blur(3px);
  
  @media (max-width: 768px) {
    z-index: 9990;
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

// New styled components for emoji picker
const EmojiButton = styled(GuidelineButton)`
  margin-left: 4px;
  margin-right: 5px;
  padding: 8px;
  color: var(--text-dim);
  position: relative;
  
  &:hover {
    color: var(--text-light);
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  ${props => props.$active && `
    color: var(--text-light);
    background-color: rgba(255, 255, 255, 0.15);
  `}
  
  svg {
    font-size: 16px;
  }
  
  @media (max-width: 768px) {
    margin-left: 2px;
    padding: 6px;
  }
`;

const EmojiPickerContainer = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  margin: 0 auto 10px auto;
  width: 90%;
  max-width: 320px;
  background-color: rgba(26, 26, 26, 0.95);
  border-radius: 12px;
  padding: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
  z-index: 2000;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-light);
  
  h3 {
    color: var(--text-light);
    font-weight: 500;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    font-size: 13px;
    
    svg {
      margin-right: 6px;
      color: var(--primary-light);
    }
  }

  @media (max-width: 768px) {
    position: fixed;
    top: auto;
    bottom: 15vh;
    left: 0;
    right: 0;
    margin: 0 auto;
    width: 85%;
    max-width: 300px;
    z-index: 10000;
    max-height: 40vh;
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 8px;
  }
`;

const EmojiPickerWrapper = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  width: 100%;
  z-index: 2000;
  
  @media (max-width: 768px) {
    position: fixed;
    bottom: auto;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    pointer-events: none;
    
    & > * {
      pointer-events: auto;
    }
  }
`;

const EmojiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
  max-height: 170px;
  overflow-y: auto;
  padding: 4px;
  
  &::-webkit-scrollbar {
    width: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.25);
  }
  
  @media (max-width: 768px) {
    max-height: 35vh;
    grid-template-columns: repeat(5, 1fr);
    gap: 5px;
  }
`;

const EmojiItem = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: scale(1.05);
  }
  
  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
  
  span {
    font-size: 9px;
    color: var(--text-dim);
    margin-top: 2px;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
  
  @media (max-width: 768px) {
    img {
      width: 25px;
      height: 25px;
    }
    
    span {
      font-size: 8px;
    }
  }
`;

const CommentForm = ({ 
  onSubmit, 
  onCancel, 
  placeholder = "Add a comment...", 
  buttonText = "Post",
  initialValue = "",
  hideGuidelines = false
}) => {
  // Convert initialValue to string if it's an object
  const initialValueStr = typeof initialValue === 'object' ? 
    JSON.stringify(initialValue) : 
    (initialValue || "");
    
  const [content, setContent] = useState(initialValueStr);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const formRef = useRef(null);
  const textareaRef = useRef(null);
  
  // Get emoji data from utility
  const customEmojis = availableEmojis;

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile(); 
    
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const maxHeight = isMobile ? 100 : 150;
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, maxHeight)}px`;
    }
  }, [content, isMobile]);
  
  // Focus textarea when form is shown on desktop only
  useEffect(() => {
    if (textareaRef.current && !isMobile) {
      textareaRef.current.focus();
    }
  }, [isMobile]);
  
  // Add this useEffect to manage focus when emoji picker opens
  useEffect(() => {
    if (showEmojiPicker && textareaRef.current) {
      // Make sure the textarea remains focused when emoji picker opens
      textareaRef.current.focus();
    }
  }, [showEmojiPicker]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!content.trim()) return;
    
    setIsSubmitting(true);
    
    try {
      await onSubmit(content);
      setContent('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const toggleGuidelines = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowGuidelines(!showGuidelines);
    setShowEmojiPicker(false);
  };

  const toggleEmojiPicker = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Close guidelines if open
    if (showGuidelines) {
      setShowGuidelines(false);
    }
    
    // Toggle emoji picker with a slight delay for smoother animation
    if (showEmojiPicker) {
      setShowEmojiPicker(false);
    } else {
      // Ensure textarea is focused before showing picker
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
      setShowEmojiPicker(true);
    }
  };

  // Insert emoji at cursor position
  const insertEmoji = (emojiName, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const emojiCode = `:${emojiName}:`;
    const textarea = textareaRef.current;
    
    if (!textarea) return;
    
    const cursorPos = textarea.selectionStart;
    
    // Get text before and after cursor
    const textBefore = content.substring(0, cursorPos);
    const textAfter = content.substring(cursorPos);
    
    // Update content with emoji inserted at cursor position
    const newContent = textBefore + emojiCode + textAfter;
    setContent(newContent);
    
    // Close emoji picker after selecting an emoji
    setShowEmojiPicker(false);
    
    // Focus back on textarea and set cursor position after the inserted emoji
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = cursorPos + emojiCode.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 10);
  };

  // Update the click outside handler to better handle emoji picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        if (showEmojiPicker) {
          setShowEmojiPicker(false);
        }
        if (showGuidelines) {
          setShowGuidelines(false);
        }
      }
    };

    if (showGuidelines || showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      
      // Add this to handle mobile touch events better
      if (isMobile) {
        document.addEventListener('touchstart', handleClickOutside);
      }
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (isMobile) {
        document.removeEventListener('touchstart', handleClickOutside);
      }
    };
  }, [showGuidelines, showEmojiPicker, isMobile]);
  
  return (
    <FormContainer onSubmit={handleSubmit} ref={formRef}>
      <InputWrapper>
        <EmojiButton 
          type="button"
          onClick={toggleEmojiPicker}
          $active={showEmojiPicker}
          aria-label="Insert Emoji"
        >
          <FontAwesomeIcon icon={faSmile} />
        </EmojiButton>

        <TextArea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={placeholder}
          autoFocus={!isMobile}
        />
        
        {!hideGuidelines && (
          <GuidelineButton 
            type="button"
            onClick={toggleGuidelines}
            $active={showGuidelines}
            aria-label="Community Guidelines"
          >
            <FontAwesomeIcon icon={faHandshake} />
          </GuidelineButton>
        )}
        
        {onCancel && (
          <button 
            type="button" 
            onClick={onCancel} 
            style={{ 
              background: 'transparent', 
              border: 'none', 
              color: 'var(--text-dim)',
              padding: '0 10px',
              cursor: 'pointer'
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
        
        <SubmitButton 
          type="submit" 
          disabled={!content.trim() || isSubmitting}
        >
          {isSubmitting ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <FontAwesomeIcon icon={faPaperPlane} />
          )}
        </SubmitButton>
      </InputWrapper>
      
      {showEmojiPicker && (
        <EmojiPickerWrapper>
          {isMobile ? (
            <>
              <Overlay onClick={(e) => {
                e.stopPropagation();
                toggleEmojiPicker(e);
              }} />
              <EmojiPickerContainer onClick={(e) => e.stopPropagation()}>
                <h3>
                  <FontAwesomeIcon icon={faSmile} />
                  Emojis
                </h3>
                <EmojiGrid>
                  {customEmojis.map(emoji => (
                    <EmojiItem 
                      key={emoji} 
                      onClick={(e) => insertEmoji(emoji, e)}
                      title={`:${emoji}:`}
                    >
                      <img src={`/emojis/${emoji}.png`} alt={emoji} />
                      <span>{emoji}</span>
                    </EmojiItem>
                  ))}
                </EmojiGrid>
              </EmojiPickerContainer>
            </>
          ) : (
            <EmojiPickerContainer onClick={(e) => e.stopPropagation()}>
              <h3>
                <FontAwesomeIcon icon={faSmile} />
                Emojis
              </h3>
              <EmojiGrid>
                {customEmojis.map(emoji => (
                  <EmojiItem 
                    key={emoji} 
                    onClick={(e) => insertEmoji(emoji, e)}
                    title={`:${emoji}:`}
                  >
                    <img src={`/emojis/${emoji}.png`} alt={emoji} />
                    <span>{emoji}</span>
                  </EmojiItem>
                ))}
              </EmojiGrid>
            </EmojiPickerContainer>
          )}
        </EmojiPickerWrapper>
      )}
      
      {showGuidelines && !hideGuidelines && (
        <>
          {isMobile && <Overlay onClick={toggleGuidelines} />}
          <GuidelinesPopup>
            <h3>
              <FontAwesomeIcon icon={faHandshake} />
              Community Guidelines
            </h3>
            <p>Please keep comments:</p>
            <ul>
              <li>Free of harassment or hate speech</li>
              <li>Relevant to the content</li>
              <li>Free of spam or self-promotion</li>
            </ul>
            <button onClick={toggleGuidelines}>Got it</button>
          </GuidelinesPopup>
        </>
      )}
    </FormContainer>
  );
};

export default CommentForm; 